import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { HandCoins, QrCode, Wallet } from "lucide-react";

import { isKeycloakAuthenticated, keycloakLogin } from "@/core/auth";
import { isPasswordGrantMode } from "@/core/auth/password-grant";
import { Logo } from "@/core/components/layout/logo";
import { ModeToggle } from "@/core/components/layout/mode-toggle";
import { AppVersionLabel, LoadScreen } from "@/core/components/shared";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useSessionStore } from "@/core/stores/session.store";
import { LoginForm } from "@/features/auth/components/login-form";

/**
 * Page `/login` — deux modes selon la config (`@/core/auth/password-grant`) :
 *
 * - **password** (client Keycloak confidentiel, UAT) : écran split-screen —
 *   panneau branding à gauche, formulaire username/password à droite.
 * - **sso** (client public, prod) : coquille de redirection vers la
 *   page de login hébergée Keycloak (Authorization Code + PKCE).
 */
export function LoginPage() {
  const { t } = useTypedTranslation();

  const authenticated = isPasswordGrantMode()
    ? Boolean(useSessionStore.getState().accessToken)
    : isKeycloakAuthenticated();

  useEffect(() => {
    if (!isPasswordGrantMode() && !isKeycloakAuthenticated()) keycloakLogin();
  }, []);

  // Déjà authentifié (session restaurée) : inutile de se reconnecter.
  if (authenticated) return <Navigate to="/app" replace />;

  if (isPasswordGrantMode()) {
    return (
      <div className="grid min-h-screen lg:grid-cols-2">
        {/* Panneau gauche — logo, textes branding et formulaire sur fond blanc.
            text-foreground explicite : le GuestLayout parent hérite d'un
            texte clair (brand-50) prévu pour fond foncé. */}
        <main className="flex min-h-screen min-w-0 flex-col bg-background px-4 py-8 text-foreground sm:px-10">
          <div className="flex items-center justify-between gap-4">
            <Logo />
            <ModeToggle />
          </div>
          <h1 className="mt-6 max-w-md text-2xl font-bold leading-tight text-foreground">
            {t("layout.footer.tagline")}
          </h1>
          {/* Formulaire centré dans l'espace disponible. */}
          <div className="flex flex-1 flex-col items-center justify-center py-8">
            <LoginForm />
            <AppVersionLabel className="mt-4 text-center" />
          </div>
          {/* Points produit + mention, en bas de page. */}
          <div className="flex flex-col gap-6">
            <ul className="flex flex-col gap-4 text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-6">
              <li className="flex items-center gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-gradient-accent text-brand-950">
                  <Wallet className="size-4" />
                </span>
                {t("auth.login.brand.point.wallet")}
              </li>
              <li className="flex items-center gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-gradient-accent text-brand-950">
                  <QrCode className="size-4" />
                </span>
                {t("auth.login.brand.point.qr")}
              </li>
              <li className="flex items-center gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-gradient-accent text-brand-950">
                  <HandCoins className="size-4" />
                </span>
                {t("auth.login.brand.point.payroll")}
              </li>
            </ul>
            <p className="text-sm text-muted-foreground">
              {t("auth.login.brand.caption")}
            </p>
          </div>
        </main>

        {/* Panneau droit — animation gif seule, masquée sur mobile. */}
        <aside className="relative hidden overflow-hidden bg-brand-950 lg:block">
          <img
            src="/1.gif"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </aside>
      </div>
    );
  }

  return (
    <div
      role="status"
      className="relative flex min-h-screen flex-col items-center justify-center gap-4"
    >
      <div className="absolute right-4 top-4">
        <ModeToggle className="text-brand-50 hover:bg-brand-800 hover:text-brand-50" />
      </div>
      <LoadScreen />
      <p className="text-sm text-brand-100">{t("auth.login.redirecting")}</p>
      <AppVersionLabel className="text-center text-brand-100" />
    </div>
  );
}
